import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, memo } from "react";

const AudioSyncEngine = lazy(() =>
  import("../components/audio/AudioSyncEngine").then((m) => ({
    default: m.AudioSyncEngine,
  })),
);

const ServerTtsEngine = lazy(() =>
  import("../components/audio/ServerTtsEngine").then((m) => ({
    default: m.ServerTtsEngine,
  })),
);
import { useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Settings2, Activity, Zap, Brain, MessageSquare, Clock, WifiOff, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

import { useDebateStore } from "../store/debateStore";
import { selectSessionLifecycle, selectConsensusGauge } from "../store/selectors";

import { AppHeader } from "../components/header/AppHeader";
import { WizardModal } from "../components/wizard/WizardModal";
import { ClarificationModal } from "../components/wizard/ClarificationModal";
import { RoundtableOrchestrator } from "../components/orchestrator/RoundtableOrchestrator";
import { RealTimeTranscript } from "../components/transcript/RealTimeTranscript";
import { UserChatDock } from "../components/interject/UserChatDock";
import {
  PresentModeShell,
  BigSpeakerCompactSlot,
} from "../components/present/PresentModeShell";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { t } from "../i18n";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useDiscussionStream } from "../hooks/useDiscussionStream";

const VerdictPanel = lazy(() => import("../components/verdict/VerdictPanel"));

// Konsey Cognitive Renaissance — Bölüm D4. Lazy çünkü brain feature
// disabled iken bundle'a bile girmesin.
const BrainModal = lazy(() =>
  import("../components/brain/BrainModal").then((m) => ({
    default: m.BrainModal,
  })),
);

/**
 * Non-blocking banner that appears at the top of the debate view when the
 * SSE client is waiting between reconnect attempts. Does NOT interrupt the
 * debate — TTS, transcript, and verdict polling continue unaffected.
 */
const NetworkRecoveryBanner = memo(function NetworkRecoveryBanner() {
  const reconnecting = useDebateStore((s) => s.reconnecting);
  const attempt = useDebateStore((s) => s.reconnectAttempt);

  if (!reconnecting) return null;

  return (
    <motion.div
      key="reconnect-banner"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="flex items-center gap-2.5 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 backdrop-blur-sm"
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0 text-amber-400" />
      <span>
        Sunucu bağlantısı geçici olarak kesildi. Yeniden bağlanılıyor
        <span className="ml-1 font-semibold text-amber-300">({attempt}. deneme)</span>
        …
      </span>
      <span className="ml-auto animate-spin text-amber-400">
        <RefreshCw className="h-3 w-3" />
      </span>
    </motion.div>
  );
});

/**
 * Inline banner for recoverable mid-debate stream drops.
 * Shows a "Yeniden Bağlan" button that re-opens the stream without wiping state.
 */
const RecoverableErrorBanner = memo(function RecoverableErrorBanner() {
  const error = useDebateStore((s) => s.error);
  const status = useDebateStore((s) => s.status);
  const resetForNew = useDebateStore((s) => s.resetForNew);

  const isRecoverable =
    status === "error" &&
    error !== null &&
    (error as Error & { recoverable?: boolean }).recoverable === true;

  if (!isRecoverable) return null;

  return (
    <motion.div
      key="recoverable-error"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="flex items-center justify-between gap-3 rounded-lg border border-orange-400/30 bg-orange-500/10 px-3 py-2.5 text-xs text-orange-200"
    >
      <div className="flex items-center gap-2">
        <WifiOff className="h-3.5 w-3.5 shrink-0 text-orange-400" />
        <span>{error.message}</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="shrink-0 border-orange-400/40 text-orange-200 hover:bg-orange-500/20"
        onClick={() => resetForNew({ keepWizard: true })}
      >
        <RefreshCw className="mr-1.5 h-3 w-3" />
        Sıfırla
      </Button>
    </motion.div>
  );
});

function isAdminMode(): boolean {
  if (typeof window === "undefined") return false;
  const flag = window.localStorage.getItem("MACDM_DEV_MODE");
  if (flag === "1") return true;
  const params = new URLSearchParams(window.location.search);
  return params.get("dev") === "1";
}

function VerdictFallback() {
  return (
    <Card
      tone="elevated"
      className="grid h-full place-items-center px-6 py-10 text-sm text-fg-muted"
    >
      Karar paneli yükleniyor…
    </Card>
  );
}

/**
 * ConfigurationCallout — floating CTA shown above the roundtable when the
 * session is in `draft` and the wizard modal is closed. Lets users re-open
 * the wizard at any time without scrolling.
 */
function ConfigurationCallout() {
  const lifecycle = useDebateStore(selectSessionLifecycle);
  const wizardOpen = useDebateStore((s) => s.wizardOpen);
  const openWizard = useDebateStore((s) => s.openWizard);
  const personasReady = useDebateStore((s) => s.wizard.personas.length > 0);

  if (lifecycle.status !== "draft") return null;
  if (wizardOpen) return null;

  return (
    <Card tone="elevated" glow className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] text-fg-subtle">
          Konsey Modu
        </div>
        <div className="mt-0.5 text-fluid-sm text-fg-strong">
          {personasReady
            ? "Konsey hazır — düzenlemek istersen sihirbazı tekrar aç."
            : "Tartışmaya başlamak için sihirbazla konseyi yapılandır."}
        </div>
      </div>
      <Button
        variant="primary"
        size="lg"
        leftIcon={<Settings2 className="h-4 w-4" aria-hidden />}
        onClick={openWizard}
      >
        {personasReady ? "Sihirbazı Aç" : "Konseyi Yapılandır"}
      </Button>
    </Card>
  );
}

function LiveMetricsBar() {
  const turnOrder = useDebateStore((s) => s.turnOrder);
  const turnsById = useDebateStore((s) => s.turnsById);
  const personas = useDebateStore((s) => s.personas);
  const roundIndex = useDebateStore((s) => s.roundIndex);
  const maxRounds = useDebateStore((s) => s.maxRounds);
  const status = useDebateStore((s) => s.status);
  // Konsey Cognitive Renaissance — Bölüm B2.
  // Sunum modu açıkken metrics bar'ın **içine** BigSpeakerCompact slot
  // ekleriz. Bu sayede stacking-context çakışması tamamen ortadan kalkar
  // (artık fixed overlay yok); kart akış içi → LiveMetricsBar üstüne
  // basamaz. PresentModeShell artık ayrı `<BigSpeakerStage />` çağırmaz.
  const presentMode = useDebateStore((s) => s.presentMode);
  const { scalarScore } = useDebateStore(selectConsensusGauge);
  const [tokensEst, setTokensEst] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const firstTurnStartedAt = useMemo(
    () =>
      turnOrder.reduce<number | null>((earliest, id) => {
        const startedAt = turnsById[id]?.startedAt ?? null;
        if (!startedAt) return earliest;
        return earliest === null || startedAt < earliest ? startedAt : earliest;
      }, null),
    [turnOrder, turnsById],
  );

  // Konsey Cognitive Renaissance — round-robin bütçesi düzeltmesi.
  // `personaCount * maxRounds` yalnızca round-robin "tur başına 1 söz"
  // sözleşmesinin geçerli olduğu koltuklar için bir üst sınırdır. Moderatör
  // (`_maybe_analyze_round`, engine.py) her tur sonunda round-robin
  // sırasının DIŞINDA ek bir müdahale sözü alabilir; bu sözler `turnOrder`'a
  // aynı şekilde eklenir ve dahil edilirse pay payda aşar (örn. 157/130).
  // Bu yüzden pay ve payda SADECE moderatör-olmayan (round-robin) koltukları
  // sayar — gösterge asla %100'ü "patlamış" görünecek şekilde aşmaz.
  const specialistCount = useMemo(
    () => Object.values(personas).filter((p) => p.role !== "moderator").length,
    [personas],
  );

  const completedTurns = useMemo(
    () =>
      turnOrder.filter((id) => {
        const turn = turnsById[id];
        if (!turn?.endedAt) return false;
        return personas[turn.speakerId]?.role !== "moderator";
      }).length,
    [turnOrder, turnsById, personas],
  );

  const lastEndedAt = useMemo(
    () =>
      turnOrder.reduce<number | null>((latest, id) => {
        const endedAt = turnsById[id]?.endedAt ?? null;
        if (!endedAt) return latest;
        return latest === null || endedAt > latest ? endedAt : latest;
      }, null),
    [turnOrder, turnsById],
  );

  const avgTurnLen = useMemo(() => {
    const texts = turnOrder
      .map((id) => turnsById[id]?.finalText?.length ?? 0)
      .filter((n) => n > 0);
    return texts.length > 0 ? Math.round(texts.reduce((a, b) => a + b, 0) / texts.length) : 0;
  }, [turnOrder, turnsById]);

  useEffect(() => {
    const total = turnOrder
      .map((id) => turnsById[id]?.finalText?.length ?? 0)
      .reduce((a, b) => a + b, 0);
    setTokensEst(Math.round(total / 3.8));
  }, [turnOrder, turnsById]);

  // Debate duration starts at the first actual turn, not at wizard/provisioning time.
  useEffect(() => {
    if (status !== "running" || !firstTurnStartedAt) return;
    setNowMs(Date.now());
    const t = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(t);
  }, [status, firstTurnStartedAt]);

  const pct = Math.round(scalarScore * 100);
  const progressColor =
    pct >= 85
      ? "from-emerald-500 via-emerald-400 to-teal-300"
      : pct >= 65
      ? "from-amber-500 via-amber-400 to-yellow-300"
      : "from-blue-500 via-cyan-400 to-sky-300";
  const consensusLabel =
    pct >= 85 ? "Yüksek Mutabakat" : pct >= 65 ? "Yakınsama" : "Müzakere";

  const elapsed = firstTurnStartedAt
    ? Math.max(
        0,
        Math.floor(
          ((status === "running" ? nowMs : lastEndedAt ?? nowMs) - firstTurnStartedAt) / 1000,
        ),
      )
    : 0;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const tps = elapsed > 0 ? Math.round(tokensEst / Math.max(1, elapsed / 60)) : 0;

  return (
    <div className="relative flex-shrink-0 overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-r from-black/70 via-black/60 to-black/70 backdrop-blur-xl shadow-[0_4px_24px_-8px_rgb(0_0_0/0.6)]">
      {/* Decorative top accent bar */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent" />

      <div className="flex items-stretch divide-x divide-white/[0.05]">
        {/* ── BigSpeakerCompact slot (sunum modunda) ───────────────
            Konsey Cognitive Renaissance — Bölüm B2. Yatay layout:
            avatar disc + identity bar yanyana. Stacking context aynı
            parent → LiveMetricsBar üzerine basma sorunu fizyolojik
            olarak çözülür. */}
        {presentMode && <BigSpeakerCompactSlot />}

        {/* ── HERO: Consensus gauge ─────────────────────────────── */}
        <div className="flex flex-1 min-w-0 items-center gap-4 px-5 py-3.5">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <div className="flex items-baseline gap-2.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-medium">
                {consensusLabel}
              </span>
              <span className="font-mono text-2xl tabular-nums font-bold text-white">
                {pct}
                <span className="text-base text-white/50 font-normal">%</span>
              </span>
            </div>
            <div className="relative h-1.5 w-full rounded-full bg-white/[0.08] overflow-hidden">
              <motion.div
                className={`h-full rounded-full bg-gradient-to-r ${progressColor} shadow-[0_0_8px_rgba(99,102,241,0.4)]`}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
              {/* Shimmer effect */}
              <motion.div
                className="pointer-events-none absolute inset-y-0 w-12 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                animate={{ left: ["-15%", "115%"] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </div>
        </div>

        {/* ── Metric tiles ──────────────────────────────────────── */}
        <MetricTile
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Toplam Süre"
          value={`${mm}:${ss}`}
          accent="text-cyan-300"
        />
        <MetricTile
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          label="Söz"
          value={`${completedTurns}/${specialistCount * maxRounds}`}
          accent="text-indigo-300"
        />
        <MetricTile
          icon={<Brain className="h-3.5 w-3.5" />}
          label="Token"
          value={`~${(tokensEst / 1000).toFixed(1)}K`}
          accent="text-violet-300"
          {...(tps > 0 ? { sublabel: `${tps}/dk` } : {})}
        />
        <MetricTile
          icon={<Activity className="h-3.5 w-3.5" />}
          label="Ort. Uzunluk"
          value={`${avgTurnLen}`}
          unit="kr"
          accent="text-amber-300"
        />
        <MetricTile
          icon={<Zap className="h-3.5 w-3.5" />}
          label="Tur"
          value={`${Math.max(1, roundIndex)}/${maxRounds}`}
          accent="text-emerald-300"
        />

        {/* ── Activity dots (right edge) ───────────────────────── */}
        <div className="flex items-center gap-1.5 px-5">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]"
              animate={{ opacity: [0.25, 1, 0.25], scale: [0.7, 1.3, 0.7] }}
              transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.32 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricTile({
  icon,
  label,
  value,
  unit,
  sublabel,
  accent = "text-white",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  unit?: string;
  sublabel?: string;
  accent?: string;
}) {
  return (
    <div className="flex flex-col items-start justify-center gap-0.5 px-4 py-3.5 min-w-[88px]">
      <div className="flex items-center gap-1.5">
        <span className={`${accent} opacity-80`}>{icon}</span>
        <span className="text-[9px] uppercase tracking-[0.16em] text-white/40 font-medium">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`font-mono tabular-nums text-lg font-semibold ${accent}`}>
          {value}
        </span>
        {unit ? (
          <span className="text-[10px] text-white/40 font-mono">{unit}</span>
        ) : null}
      </div>
      {sublabel ? (
        <span className="text-[9px] text-white/30 font-mono leading-none">{sublabel}</span>
      ) : null}
    </div>
  );
}

export function ConsensusModePage() {
  const lifecycle = useDebateStore(selectSessionLifecycle);
  const toggleMute = useDebateStore((s) => s.toggleMute);
  const presentMode = useDebateStore((s) => s.presentMode);
  const adminMode = useMemo(isAdminMode, []);
  const lastErrorRef = useRef<string | null>(null);
  const stream = useDiscussionStream();
  const [searchParams, setSearchParams] = useSearchParams();

  // Wire up the global keyboard shortcuts (P → present mode, ? → help, etc.).
  useKeyboardShortcuts();

  // İş Kolu — arka planda devam eden tartışma.
  //
  // Dashboard's "Devam Eden" (running) rows route here as
  // `/debate?resumeSessionId=<id>` instead of `/replay/<id>` — the debate
  // is STILL ALIVE server-side (closing the tab no longer cancels it),
  // so we re-attach to it via `resume()` instead of rendering a static
  // report. The param is stripped immediately (replace, not push) so a
  // page refresh — or starting a brand-new debate from this same tab —
  // never re-triggers a resume against a session that has since moved on.
  useEffect(() => {
    const resumeId = searchParams.get("resumeSessionId");
    if (!resumeId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("resumeSessionId");
        return next;
      },
      { replace: true },
    );
    stream.resume(resumeId).catch((err) => {
      toast.error(
        `Tartışmaya yeniden bağlanılamadı: ${err instanceof Error ? err.message : String(err)}`,
      );
    });
    // Deliberately mount-only — `resumeId` is consumed exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface stream errors as toasts (keeps the verbose alert out of the layout).
  useEffect(() => {
    if (lifecycle.error && lifecycle.error.message !== lastErrorRef.current) {
      lastErrorRef.current = lifecycle.error.message;
      toast.error(t("errors.streamFailed", { message: lifecycle.error.message }));
    }
    if (!lifecycle.error) lastErrorRef.current = null;
  }, [lifecycle.error]);

  const onToggleTheme = useCallback(() => {
    const html = document.documentElement;
    const next = html.classList.contains("dark") ? "light" : "dark";
    html.classList.toggle("dark", next === "dark");
    html.style.colorScheme = next;
    try {
      window.localStorage.setItem("MACDM_THEME", next);
    } catch {
      /* persistence is best-effort */
    }
  }, []);

  // Restore saved theme on mount (default = dark).
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem("MACDM_THEME");
      if (saved === "light") document.documentElement.classList.remove("dark");
    } catch {
      /* ignore */
    }
  }, []);

  // Konsey Cognitive Renaissance — Bölüm A2.
  // Sycophancy.alert SSE → toast banner. Strict profilde Devil's Advocate
  // injection auto-tetikleniyor; kullanıcı ne olduğunu bilsin diye uyarı.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        round: number;
        consecutive: number;
        devilAdvocateInvited?: boolean;
      }>).detail;
      if (!detail) return;
      const msg = detail.devilAdvocateInvited
        ? `Konsey aşırı uyumlu — Devil's Advocate çağrıldı (Tur ${detail.round})`
        : `Konsey aşırı uyumlu görünüyor (Tur ${detail.round}, ${detail.consecutive} ardışık alert)`;
      toast.warning(msg, {
        description: "Karşıt görüş istenmektedir; sıradaki uzman alternatif perspektifler üretecek.",
        duration: 6000,
      });
    };
    window.addEventListener("consensus:sycophancy-alert", handler);
    return () => window.removeEventListener("consensus:sycophancy-alert", handler);
  }, []);

  return (
    <TooltipPrimitive.Provider delayDuration={250} skipDelayDuration={120}>
      <div className="flex min-h-screen flex-col">
        <AppHeader
          adminMode={adminMode}
          onToggleTheme={onToggleTheme}
          onToggleMute={toggleMute}
        />

        <main
          className={
            presentMode
              ? "w-full flex-1 px-3 pt-3 pb-3 sm:px-4 lg:px-6"
              : "mx-auto w-full max-w-[120rem] flex-1 px-4 py-6 sm:px-6 lg:px-8"
          }
        >
          {/* Network resilience banners — visible in all layouts */}
          <div className="mb-3 flex flex-col gap-2">
            <NetworkRecoveryBanner />
            <RecoverableErrorBanner />
          </div>

          {presentMode ? (
            // ── Cinematic, full-bleed layout ────────────────────────────
            // Three rows: hero stage (fills) ▸ live metrics strip
            // Two cols : 8/12 stage  | 4/12 transcript
            // No outer wrapper padding; the inner cards keep the breathing
            // room so the metrics bar can reach edge-to-edge for visual
            // weight at the bottom.
            //
            // `--present-right-col-w` is consumed by the `PresentModeShell`
            // overlay so the floating TopicBanner clears the transcript card
            // boundary on every viewport size (no more visual collisions).
            <div
              className="grid h-[calc(100vh-4rem-1.5rem)] grid-cols-[minmax(0,1.9fr)_minmax(360px,0.85fr)] gap-3"
              style={{
                ["--present-right-col-w" as string]:
                  "max(360px, calc(0.31 * (100vw - 60px)))",
              }}
            >
              <section className="flex min-w-0 flex-col gap-3 overflow-hidden">
                <div className="flex-1 min-h-0 overflow-hidden">
                  <RoundtableOrchestrator />
                </div>
                <LiveMetricsBar />
              </section>
              <aside className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[hsl(220_22%_6%/0.85)] backdrop-blur-xl shadow-[0_8px_40px_-12px_rgb(0_0_0/0.6)]">
                <RealTimeTranscript />
              </aside>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]">
              <section className="flex min-w-0 flex-col gap-4">
                <ConfigurationCallout />
                <RoundtableOrchestrator />
                <RealTimeTranscript />
              </section>

              <aside className="min-w-0 lg:sticky lg:top-[calc(var(--header-h)+1rem)] lg:self-start">
                <Suspense fallback={<VerdictFallback />}>
                  <VerdictPanel />
                </Suspense>
              </aside>
            </div>
          )}
        </main>

        <WizardModal />
        {/*
         * Mounted as a sibling of `WizardModal`, NOT inside it — a 409
         * needs_clarification gate can be hit from the header's direct
         * "Tartışmayı Başlat" CTA (`AppHeader.onStart`) whenever personas
         * are already staged, which never opens `WizardModal`'s Radix
         * `Dialog.Root`. Radix unmounts closed dialog content, so nesting
         * this inside `SetupWizard` meant the prompt silently vanished
         * on that path — `clarification` state was set but nothing in
         * the tree was listening (2026-07-22).
         */}
        <ClarificationModal />
        <UserChatDock />
        <PresentModeShell />
        <Suspense fallback={null}>
          <BrainModal />
        </Suspense>
        <Suspense fallback={null}>
          <AudioSyncEngine />
          <ServerTtsEngine />
        </Suspense>

        <Toaster
          position="top-right"
          theme="dark"
          richColors
          offset={20}
          toastOptions={{
            className: "glass-strong text-fg-strong",
          }}
        />
      </div>
    </TooltipPrimitive.Provider>
  );
}
